import debounce from "debounce";
import React, { useCallback, useEffect, useRef } from 'react';
import LoadingBox from "../../../../../../components/LoadingBox";
import { PROCESS_TYPE } from "../../../../../../util/varibles/constants";
import { IRoundPlan, TTaskTreatment } from "../../../../../../util/varibles/interface";
import { callEventEditOp, getEditOp, IUnitWhenEdit, useEditOp } from "../../EditOperationContext";
import { checkTaskOfTreatment, moveUnitToNewRound } from "../constants";
import Round from "./Round";
import Tank from "./Tank";


interface IProps {
    units: IUnitWhenEdit[]
}

const ROUND_MAP_UPDATE_DELAY = 600;

const RoundMap: React.FC<IProps> = React.memo(({ units }) => {
    const [round_map = {}, set] = useEditOp(state => state.operation?.round_map);
    const [loadingRoundMap] = useEditOp(state => state.loadingRoundMap);
    const { getRoundMap, getLocations, turnOnCalculate } = callEventEditOp();
    const getRoundMapRef = useRef(getRoundMap);
    const getLocationsRef = useRef(getLocations);
    const turnOnCalculateRef = useRef(turnOnCalculate);
    const updateRoundMapRef = useRef(debounce((units: any[]) => {
        getRoundMapRef.current({ units, after_round: {} });
    }, ROUND_MAP_UPDATE_DELAY));
    const get = getEditOp();

    useEffect(() => {
        getRoundMapRef.current = getRoundMap;
        getLocationsRef.current = getLocations;
        turnOnCalculateRef.current = turnOnCalculate;
    }, [getRoundMap, getLocations, turnOnCalculate]);

    useEffect(() => () => updateRoundMapRef.current.clear(), []);

    const handleGetLocations = useCallback(() => {
        getLocationsRef.current();
    }, []);

    useEffect(() => {
        if (units.length === 0)
            return;

        const value = units.reduce((rs: any[], item) => {
            const {
                id,
                unit_id,
                total_weight = 0,
                avg_weight = 0,
                tasks = [],
                density_percent,
                tank_number,
                process_type = PROCESS_TYPE.SEQUENCE
            } = item;
            if (!total_weight || !avg_weight)
                return rs;

            const jobs = (tasks as TTaskTreatment[]).filter(job =>
                job.type != null && !checkTaskOfTreatment(job).isError
            );

            if (jobs.length === 0)
                return rs;

            rs.push({ id, unit_id, avg_weight, total_weight, density_percent, tank_number, tasks: jobs, process_type });

            return rs;
        }, []);
        if (value.length === 0) {
            updateRoundMapRef.current.clear();
            set(({ operation }) => ({ operation: operation ? { ...operation, round_map: {} } : operation }));
        } else {
            updateRoundMapRef.current(value);
        }
    }, [units, set]);

    const handleChangeTank = (data: IRoundPlan['tanks'][0], isAdd: boolean) => {
        const { id_unit, start_places = [] } = data;
        const { units } = get();
        const id = id_unit || (start_places[0]
            ? Object.keys(units).find(key => units[key].unit_id === start_places[0])
            : undefined);
        if (id) {
            const tank_number = Object.keys(round_map).reduce((rs: number, key) => {
                return rs + round_map[key].tanks.filter(item => item.id_unit === id || item.start_places?.[0] === start_places?.[0]).length;
            }, 0)

            set(({ units }) => {
                const { density_percent, ...args } = units[id];
                return {
                    units: { ...units, [id]: { ...args, tank_number: isAdd ? tank_number + 1 : tank_number - 1 } },
                }
            })
            turnOnCalculateRef.current();
        }
    }

    const getTankUnitId = (data: IRoundPlan['tanks'][0], source: Record<string, IUnitWhenEdit>) => {
        const { id_unit, start_places = [] } = data;
        return id_unit || (start_places[0]
            ? Object.keys(source).find(key => source[key].unit_id === start_places[0])
            : undefined);
    }

    const handleSeparate = (data: IRoundPlan['tanks'][0]) => {
        const { units: unitMap } = get();
        const id = getTankUnitId(data, unitMap);
        if (!id)
            return;

        const orderedIds = units.map(unit => unit.id);
        const selectedIndex = orderedIds.indexOf(id);
        if (selectedIndex < 0)
            return;

        set(({ units }) => ({ units: moveUnitToNewRound(units, orderedIds, id) }));
        turnOnCalculateRef.current();
    }

    const handleParallel = (currentRound: string, nextRound?: string) => {
        const currentTanks = round_map[currentRound]?.tanks;
        const nextTanks = nextRound ? round_map[nextRound]?.tanks : undefined;
        if (currentTanks && nextTanks) {
            const ids = new Set([...currentTanks, ...nextTanks].map(item => item.start_places?.[0]));
            ids.delete(undefined);
            set(({ units }) => {
                return {
                    units: Object.keys(units).reduce((rs: { [id: string]: IUnitWhenEdit }, key) => {
                        const { unit_id } = units[key]
                        rs[key] = ids.has(unit_id)
                            ? { ...units[key], process_type: PROCESS_TYPE.PARALLEL }
                            : units[key];
                        return rs;
                    }, {})
                }
            })

            turnOnCalculateRef.current();
        }
    }

    return <div className={[
        'relative flex flex-col gap-[8px] px-[24px] pb-2',
        loadingRoundMap ? 'min-h-[80px]' : ''
    ].join(' ')}>
        {units.length > 0 && <>
            {loadingRoundMap && <LoadingBox loading level='bg' />}
            {Object.keys(round_map).map((key, i, arr) => {
                const { after_processes, tanks } = round_map[key];
                const nextRound = arr[i + 1];
                const currentUnits = get().units;
                const firstProcessingUnitId = tanks.reduce<string | undefined>((result, tank) => {
                    if (result || !tank.total_weight)
                        return result;
                    return getTankUnitId(tank, currentUnits);
                }, undefined);
                return <Round
                    key={key}
                    roundId={key}
                    after_processes={after_processes}
                    getLocations={handleGetLocations}
                    onUpdateAfterRound={value => {
                        const { after_round = {} } = get().bodyGetRoundMap || {};
                        turnOnCalculateRef.current()
                        setTimeout(() => getRoundMapRef.current({ after_round: { ...after_round, ...value } }), 10);
                    }}
                >
                    <div className='grid gap-3 z-[1]' style={{ gridTemplateColumns: `repeat(${tanks.length}, 1fr)` }}>
                        {tanks.map((item, i: number) => {
                            const unitId = getTankUnitId(item, currentUnits);
                            const canSeparate = !!item.total_weight
                                && !!unitId
                                && !!firstProcessingUnitId
                                && unitId !== firstProcessingUnitId;
                            return <Tank
                                key={i}
                                data={item}
                                nextUnit={!!round_map[nextRound]}
                                canSeparate={canSeparate}
                                onParallel={() => handleParallel(key, nextRound)}
                                onSeparate={() => handleSeparate(item)}
                                onChangeTank={(isAdd) => handleChangeTank(item, isAdd)}
                            />
                        })}
                    </div>
                </Round>
            })}
        </>}
    </div>;
}, (prev, next) => JSON.stringify(prev.units) === JSON.stringify(next.units));

export default RoundMap;
